import { ReactElement, useEffect } from 'react'
import { Form, Input, Button, Checkbox, Flex, Card, Alert } from 'antd'
import { UserOutlined, LockOutlined } from '@ant-design/icons'
import { useNavigate, useLocation } from 'react-router-dom'
import { connect } from 'react-redux'
import { login, clearLoginError } from 'actions/auth'
import { isDemoMode } from 'lib/is-demo'
import { isLoggingIn, getLoginError, isUserLoggedIn } from 'selectors/auth'
import styles from './Login.module.scss'
import virtuallogIcon from '../style/img/virtuallog.svg'

interface LoginFormValues {
  username: string
  password: string
  // remember: boolean // TODO: Activate when backend supports "Remember me" functionality
}

interface ReduxState {
  loggingIn: boolean
  loginError: string | null
  userLoggedIn: boolean
}

const select = (): ReduxState => ({
  loggingIn: isLoggingIn(),
  loginError: getLoginError(),
  userLoggedIn: isUserLoggedIn(),
})

const Login = (props: ReduxState): ReactElement => {
  const { loggingIn, loginError, userLoggedIn } = props
  const navigate = useNavigate()
  const location = useLocation()

  // Get the path the user was trying to access before being redirected to login
  const from = (location.state as { from?: string })?.from || '/'

  // If user is already logged in, redirect to home or intended destination
  useEffect(() => {
    if (userLoggedIn) {
      navigate(from, { replace: true })
    }
  }, [userLoggedIn, navigate, from])

  // Clear login error when component unmounts
  useEffect(() => {
    return () => {
      clearLoginError()
    }
  }, [])

  const onFinish = (values: LoginFormValues): void => {
    login(values.username, values.password, navigate)
  }

  return (
    <Flex className={styles.container} justify="center" align="center">
      <Card className={styles.loginCard}>
        <Flex vertical gap={24} align="center">
          {/* Logo */}
          <img
            src={virtuallogIcon}
            alt="VirtualLog"
            className={styles.logo}
          />

          {/* Error Alert */}
          {loginError && (
            <Alert
              message="Login Failed"
              description={loginError}
              type="error"
              closable
              onClose={clearLoginError}
              showIcon
            />
          )}

          {/* Login Form */}
          <Form
            name="login"
            className={styles.loginForm}
            initialValues={isDemoMode ? { username: 'admin', password: 'admin' } : undefined}
            // initialValues={{ remember: true }} // TODO: Activate when backend supports "Remember me"
            onFinish={onFinish}
            autoComplete="off"
          >
            <Form.Item
              name="username"
              rules={[
                { required: true, message: 'Please input your username!' }
              ]}
            >
              <Input
                prefix={<UserOutlined />}
                placeholder="Username"
                size="large"
              />
            </Form.Item>

            <Form.Item
              name="password"
              rules={[
                { required: true, message: 'Please input your password!' }
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Password"
                size="large"
              />
            </Form.Item>

            {/* TODO: Activate when backend supports "Remember me" and "Forgot password" functionality */}
            {/* <Form.Item>
              <Flex justify="space-between" align="center">
                <Form.Item name="remember" valuePropName="checked" noStyle>
                  <Checkbox>Remember me</Checkbox>
                </Form.Item>
                <a href="#" className={styles.forgotPassword}>
                  Forgot password?
                </a>
              </Flex>
            </Form.Item> */}

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                block
                loading={loggingIn}
                disabled={loggingIn}
              >
                {loggingIn ? 'Logging in...' : 'Log in'}
              </Button>
            </Form.Item>
          </Form>

          {/* Legal Links */}
          <div className={styles.legalLinks}>
            <a href="https://virtuallog.io/privacy.html" target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </a>
            <span className={styles.legalDivider}>|</span>
            <a href="https://virtuallog.io/terms.html" target="_blank" rel="noopener noreferrer">
              Terms of Service
            </a>
          </div>
        </Flex>
      </Card>
    </Flex>
  )
}

export default connect(select)(Login)
